import React, { ReactElement, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { css } from '@emotion/react';
import { FileData, ImageUploadField } from '@bucketplace/commerce-admin-ui';

import { useEditorInfoContext } from '../../../contexts';
import { useFieldForm } from '../../../hooks';
import { FieldItem } from '../../FieldItem';
import { URLInput } from '../URLInput';
import { validateImageURL } from './utils';

const PATH = 'uploads/exhibitions/contents';
/** 최대 업로드 수 (기존에도 1개 단위로 업로드) */
const DEFAULT_MAX_COUNT = 1;
const DEFAULT_LIMIT = 10 * 1024;
const ACCEPT = '.jpg, .jpeg, .png, .webp, .gif';

export function ImageField({
  name,
  label,
  placeholder = '이미지 URL 입력',
}: {
  name: string,
  label?: string,
  placeholder?: string,
}): ReactElement {
  const { uploadEndpoint } = useEditorInfoContext();
  const { setValue, watch } = useFormContext();
  const {
    fields,
    errors,
    register,
    onChange,
    trigger,
    setValue: setStateValue,
  } = useFieldForm();

  const file: string | undefined = watch(name);
  const errorMessage = errors['url'];

  // Textarea에 동기화
  const handleAfterUpload = (value?: FileData) => {
    setStateValue('url', value?.url ?? '');
  };

  // 업로드된 Image 삭제 시,
  useEffect(() => {
    if (!file) {
      setStateValue('url', '');
    }
  }, [file]);

  useEffect(() => {
    register('url', {
      defaultValue: file,
      validate: async (value) => {
        if (!value) { return; }
        try {
          await validateImageURL(value);
        } catch (e) {
          return '유효하지 않은 이미지 URL입니다.';
        }
      },
    });
  }, []);

  return (
    <>
      <ImageUploadField
        name={name}
        label={label}
        endpoint={uploadEndpoint}
        path={PATH}
        accept={ACCEPT}
        maxCount={DEFAULT_MAX_COUNT}
        fileLimitSize={DEFAULT_LIMIT}
        useArrayType={false}
        serializer={{
          serialize: (url: string) => ({ url }),
          deserialize: (data) => data.url,
        }}
        customCss={ImageUploadFieldCss}
        onAfterUpload={handleAfterUpload}
      />
      <FieldItem
        error
        hint={errorMessage}
        hintAlign="end"
      >
        <URLInput
          value={fields['url']}
          readOnly={!!file}
          placeholder={placeholder}
          onChange={(value) => {
            onChange('url', value);
          }}
          onBlur={async () => {
            if (!await trigger('url') && fields['url']) {
              setValue(name, fields['url']);
            }
          }}
        />
      </FieldItem>
    </>
  );
}

const ImageUploadFieldCss = css`
  margin-bottom: 4px;
`;
